/* eslint-disable @next/next/no-img-element */
"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import type { Dictionary, Locale } from "@/app/[lang]/dictionaries";

function stripLocale(pathname: string, lang: Locale) {
  const withoutLocale = pathname.replace(new RegExp(`^/${lang}`), "");
  return withoutLocale === "" ? "/" : withoutLocale;
}

export default function SiteFooter({ lang, dict }: { lang: Locale; dict: Dictionary["footer"] }) {
  const pathname = usePathname();
  const currentPath = stripLocale(pathname, lang);

  const linkClass = (href: string) =>
    `font-body text-sm transition-colors ${
      currentPath === href
        ? "text-[#ECFDF5]"
        : "text-[rgba(209,250,229,0.7)] hover:text-[#ECFDF5]"
    }`;

  return (
    <footer className="w-full bg-[#022C22] border-t border-[rgba(6,78,59,0.3)]">
      <div className="px-12 py-20 max-w-screen-2xl mx-auto flex flex-col gap-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12">

          {/* Column 1 — Brand */}
          <div className="flex flex-col gap-6">
            <span className="text-[#ECFDF5] font-headline font-bold text-lg tracking-[0.45px]">
              {dict.brand}
            </span>
            <p className="text-[rgba(209,250,229,0.7)] font-body text-sm leading-[1.65]">
              {dict.addressLine2}
            </p>
          </div>

          {/* Column 2 — Product */}
          <div className="flex flex-col gap-6">
            <span className="text-[#10B981] font-headline font-bold text-sm tracking-[1.4px] uppercase">
              {dict.productHeading}
            </span>
            <ul className="flex flex-col gap-1">
              <li className="py-[2px]">
                <Link href={`/${lang}`} className={linkClass("/")}>
                  {dict.productRing}
                </Link>
              </li>
              <li className="py-[2px]">
                <Link href={`/${lang}/retreat`} className={linkClass("/retreat")}>
                  {dict.productRetreat}
                </Link>
              </li>
            </ul>
          </div>

          {/* Column 3 — Support */}
          <div className="flex flex-col gap-6">
            <span className="text-[#10B981] font-headline font-bold text-sm tracking-[1.4px] uppercase">
              {dict.supportHeading}
            </span>
            <ul className="flex flex-col gap-4">
              <li className="py-[2px]">
                <Link href={`/${lang}/privacy-policy`} className={linkClass("/privacy-policy")}>
                  {dict.supportPrivacy}
                </Link>
              </li>
              <li className="py-[2px]">
                <Link href={`/${lang}/warranty-policy`} className={linkClass("/warranty-policy")}>
                  {dict.supportWarranty}
                </Link>
              </li>
              <li className="py-[2px]">
                <Link href={`/${lang}/return-policy`} className={linkClass("/return-policy")}>
                  {dict.supportReturn}
                </Link>
              </li>
              <li className="py-[2px]">
                <Link href={`/${lang}/terms-of-service`} className={linkClass("/terms-of-service")}>
                  {dict.supportTerms}
                </Link>
              </li>
            </ul>
          </div>

          {/* Column 4 — Contact + app store badges */}
          <div className="flex flex-col gap-3">
            <span className="text-[#10B981] font-headline font-bold text-sm tracking-[1.4px] uppercase">
              {dict.contactHeading}
            </span>
            <div className="py-[2px]">
              <Link href={`/${lang}/lien-he`} className={linkClass("/lien-he")}>
                {dict.contactLink}
              </Link>
            </div>

            {/* QR code */}
            <img
              src="/qr_test.png"
              alt={dict.qrAlt}
              width={72}
              height={72}
              className="mt-1"
            />

            {/* Store badges */}
            <div className="flex flex-col gap-2">
              <a href="#" aria-label={dict.appleStoreAlt}>
                <img
                  src="/apple_store.png"
                  alt={dict.appleStoreAlt}
                  width={119}
                  height={35}
                  className="rounded-sm"
                />
              </a>
              <a href="#" aria-label={dict.googlePlayAlt}>
                <img
                  src="/google_store.png"
                  alt={dict.googlePlayAlt}
                  width={119}
                  height={34}
                />
              </a>
            </div>
          </div>
        </div>

        {/* Bottom border + copyright */}
        <div className="pt-8 border-t border-[rgba(6,78,59,0.3)]">
          <p className="text-[#047857] font-body text-xs tracking-[1.2px] uppercase">
            {dict.copyright}
          </p>
        </div>
      </div>
    </footer>
  );
}
